// src/pages/Admin/Views/Notifications/NotificationDefinitionsManager.jsx
import React, { useEffect, useState } from 'react';
import { Loader2, Pencil, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Switch } from '../../../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast';
import {
  listNotificationDefinitions,
  updateNotificationDefinition,
} from '../../../../services/notificationDefinitionsApi';

const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
const SEVERITY_BADGE = {
  CRITICAL: 'bg-red-100 text-red-800',
  WARNING: 'bg-amber-100 text-amber-800',
  INFO: 'bg-blue-100 text-blue-800',
};

const emptyForm = {
  headline_template: '',
  description_template: '',
  cta_label: '',
  cta_link_template: '',
  severity: 'WARNING',
  default_snooze_days: 7,
  help_article_url: '',
  reward_coupon_code: '',
  is_active: true,
};

export default function NotificationDefinitionsManager() {
  const { toast } = useToast();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listNotificationDefinitions();
      setDefinitions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to load definitions',
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (def) => {
    setEditing(def);
    setForm({
      headline_template: def.headline_template || '',
      description_template: def.description_template || '',
      cta_label: def.cta_label || '',
      cta_link_template: def.cta_link_template || '',
      severity: def.severity || 'WARNING',
      default_snooze_days: def.default_snooze_days ?? 7,
      help_article_url: def.help_article_url || '',
      reward_coupon_code: def.reward_coupon_code || '',
      is_active: def.is_active ?? true,
    });
  };

  const closeEdit = () => { setEditing(null); setForm(emptyForm); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        headline_template: form.headline_template,
        description_template: form.description_template,
        cta_label: form.cta_label || null,
        cta_link_template: form.cta_link_template || null,
        severity: form.severity,
        default_snooze_days: Number(form.default_snooze_days),
        help_article_url: form.help_article_url || null,
        reward_coupon_code: form.reward_coupon_code ? form.reward_coupon_code.trim() : null,
        is_active: form.is_active,
      };
      const res = await updateNotificationDefinition(editing.id, payload);
      setDefinitions((prev) => prev.map((d) => (d.id === editing.id ? res.data : d)));
      toast({ title: 'Saved', description: `"${editing.type_key}" updated.` });
      closeEdit();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Notification Content</h1>
          <p className="text-sm text-muted-foreground">
            Manage the seller action-center notifications: copy, CTA, severity, snooze,
            help article, and reward coupon.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Definitions</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : definitions.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No notification definitions found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Headline</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Reward</TableHead>
                    <TableHead>Help</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {definitions.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono text-xs">{d.type_key}</TableCell>
                      <TableCell className="max-w-[260px] truncate">{d.headline_template}</TableCell>
                      <TableCell>
                        <Badge className={SEVERITY_BADGE[d.severity] || ''} variant="secondary">{d.severity}</Badge>
                      </TableCell>
                      <TableCell>
                        {d.reward_coupon_code
                          ? <Badge variant="secondary">{d.reward_coupon_code}</Badge>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {d.help_article_url
                          ? <a href={d.help_article_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">link</a>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {d.is_active
                          ? <Badge className="bg-green-100 text-green-800" variant="secondary">Active</Badge>
                          : <Badge variant="secondary">Off</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(d)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit notification</DialogTitle>
            <DialogDescription className="font-mono text-xs">{editing?.type_key}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Headline</Label>
              <Input value={form.headline_template} onChange={(e) => setField('headline_template', e.target.value)} />
              <p className="text-xs text-muted-foreground">Supports placeholders like {'{score}'}.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description_template} onChange={(e) => setField('description_template', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>CTA label</Label>
                <Input value={form.cta_label} onChange={(e) => setField('cta_label', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>CTA link</Label>
                <Input value={form.cta_link_template} onChange={(e) => setField('cta_link_template', e.target.value)} placeholder="/seller/profile" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setField('severity', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Snooze days</Label>
                <Input type="number" min={1} value={form.default_snooze_days} onChange={(e) => setField('default_snooze_days', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Help article URL</Label>
              <Input value={form.help_article_url} onChange={(e) => setField('help_article_url', e.target.value)} placeholder="https://help.selyst.com/..." />
              <p className="text-xs text-muted-foreground">Shown to sellers as a &quot;Learn more&quot; tooltip. Leave blank for none.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Reward coupon code</Label>
              <Input value={form.reward_coupon_code} onChange={(e) => setField('reward_coupon_code', e.target.value.toUpperCase())} placeholder="e.g. PROFILE10" />
              <p className="text-xs text-muted-foreground">
                Must be an ACTIVE, seller-specific coupon. Activated once when the seller
                completes this action. Leave blank for no reward.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="is_active" checked={form.is_active} onCheckedChange={(v) => setField('is_active', v)} />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
